import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { CheckCircle, Mail, Send, KeyRound } from 'lucide-react';
import { Button } from '@core/components/ui/shared/buttons/button';
import { FloatingInput } from '@core/components/ui/shared/inputs/floating-input';
import { forgotPasswordSchema, type ForgotPasswordFormData } from '@/validations/schemas';
import useForgotPassword from '@/hooks/use-forgot-password';
import { containerVariants, itemVariants } from '@/lib/animations';

const ForgotPasswordForm = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const params: URLSearchParams = new URLSearchParams(location.search);
  const returnUrl: string = params.get('returnUrl') ?? '/';

  const { sendResetEmail, isLoading, isSuccess } = useForgotPassword(returnUrl);

  const form = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      email: '',
    },
  });

  const onSubmit = async (data: ForgotPasswordFormData): Promise<void> => {
    await sendResetEmail(data);
  }

  if (isSuccess) {
    return (
      <motion.div
        className="flex flex-col items-center gap-4 py-6 sm:gap-6 sm:py-8"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ type: 'spring', stiffness: 200, damping: 20 }}
      >
        <motion.div
          className="p-3 sm:p-4 rounded-full bg-green-500/10"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
        >
          <Mail className="h-8 w-8 sm:h-10 sm:w-10 text-green-500" />
        </motion.div>
        <motion.div
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ delay: 0.3, type: 'spring', stiffness: 200 }}
        >
          <CheckCircle className="h-16 w-16 sm:h-20 sm:w-20 md:h-24 md:w-24 text-green-500" />
        </motion.div>
        <h2 className="text-xl font-bold sm:text-2xl md:text-3xl">{t('emailSent')}</h2>
        <p className="text-center text-sm text-muted-foreground sm:text-base max-w-sm">
          {t('emailSentDescription')}
        </p>
      </motion.div>
    )
  }

  return (
    <motion.div
      className="space-y-6 sm:space-y-8"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <motion.div variants={itemVariants} className="text-center space-y-2">
        <motion.div
          className="flex justify-center mb-4"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', stiffness: 200, damping: 15 }}
        >
          <div className="p-3 sm:p-4 rounded-full bg-primary/10">
            <KeyRound className="h-8 w-8 sm:h-10 sm:w-10 text-primary" />
          </div>
        </motion.div>
        <h1 className="text-2xl font-bold sm:text-3xl md:text-4xl">{t('remindPassword')}</h1>
        <p className="text-sm text-muted-foreground sm:text-base">
          {t('forgotPasswordSubtitle')}
        </p>
      </motion.div>

      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 sm:space-y-5">
        <motion.div variants={itemVariants} className="space-y-1">
          <Controller
            name="email"
            control={form.control}
            render={({ field }) => (
              <FloatingInput
                id="email"
                type="email"
                label={t('email')}
                className="h-12 sm:h-14 text-base sm:text-lg"
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                aria-invalid={!!form.formState.errors.email}
                readOnly={isLoading}
              />
            )}
          />
          <div className="min-h-5">
            {form.formState.errors.email && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-xs sm:text-sm text-destructive"
              >
                {t(form.formState.errors.email.message!)}
              </motion.p>
            )}
          </div>
        </motion.div>

        <motion.div variants={itemVariants} className="pt-2 sm:pt-4">
          <Button
            type="submit"
            size="lg"
            className="w-full h-12 sm:h-14 text-base sm:text-lg font-semibold group"
            disabled={isLoading}
          >
            {isLoading ? (
              <motion.div
                className="h-5 w-5 border-2 border-current border-t-transparent rounded-full"
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
              />
            ) : (
              <>
                <Send className="mr-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                {t('sendResetLink')}
              </>
            )}
          </Button>
        </motion.div>
      </form>
    </motion.div>
  );
}

export default ForgotPasswordForm;